import { Block, BlockResponse } from "@/Contracts/ProposalBuilder/Block";
import { BlockSaveFailure, worseBlockSaveFailure } from "./blockSaveRequest";

export type QuoteBlockSaveFailure = BlockSaveFailure;

export interface QuoteBlockSaveSnapshot {
	pendingCount: number;
	inFlightCount: number;
	failure: QuoteBlockSaveFailure | null;
}

export interface QuoteBlockSaveBlockState {
	locked: boolean;
	failed: boolean;
	pendingBlock: Block | null;
}

export interface QuoteBlockSaverDeps {
	put: (block: Block) => Promise<BlockResponse>;
	passthrough: (block: Block) => Promise<BlockResponse>;
	isRetryableError: (error: unknown) => boolean;
	isAuthError: (error: unknown) => boolean;
	onOnline: (listener: () => void) => void;
	retryDelaysMs?: number[];
	lockAfterMs?: number;
	maxConsecutiveAuthFailures?: number;
}

export interface QuoteBlockSaver {
	save: (block: Block) => Promise<BlockResponse>;
	setEnabled: (enabled: boolean) => void;
	getSnapshot: () => QuoteBlockSaveSnapshot;
	getBlockState: (blockId: string) => QuoteBlockSaveBlockState;
	subscribe: (listener: () => void) => () => void;
	waitForInFlight: () => Promise<void>;
}

interface QueuedSave {
	block: Block;
	resolve: (response: BlockResponse) => void;
	reject: (error: unknown) => void;
}

interface BlockSaveQueue {
	saves: QueuedSave[];
	running: boolean;
	inFlight: boolean;
	locked: boolean;
	failure: QuoteBlockSaveFailure | null;
	failedBlock: Block | null;
}

export const DEFAULT_QUOTE_SAVE_RETRY_DELAYS_MS = [2000, 4000, 8000, 15000];
export const DEFAULT_QUOTE_SAVE_LOCK_AFTER_MS = 5000;
export const DEFAULT_MAX_CONSECUTIVE_AUTH_FAILURES = 3;

const UNLOCKED_BLOCK_STATE: QuoteBlockSaveBlockState = {
	locked: false,
	failed: false,
	pendingBlock: null
};

export const createQuoteBlockSaver = ({
	put,
	passthrough,
	isRetryableError,
	isAuthError,
	onOnline,
	retryDelaysMs = DEFAULT_QUOTE_SAVE_RETRY_DELAYS_MS,
	lockAfterMs = DEFAULT_QUOTE_SAVE_LOCK_AFTER_MS,
	maxConsecutiveAuthFailures = DEFAULT_MAX_CONSECUTIVE_AUTH_FAILURES
}: QuoteBlockSaverDeps): QuoteBlockSaver => {
	let enabled = false;
	const queues = new Map<string, BlockSaveQueue>();
	const listeners = new Set<() => void>();
	const retryWakers = new Set<() => void>();

	const notify = () => listeners.forEach(listener => listener());

	onOnline(() => {
		const wakers = [...retryWakers];
		retryWakers.clear();
		wakers.forEach(wake => wake());
	});

	const waitBeforeRetry = (attempt: number): Promise<void> =>
		new Promise(resolve => {
			const wake = () => {
				clearTimeout(timer);
				retryWakers.delete(wake);
				resolve();
			};
			const timer = setTimeout(
				wake,
				retryDelaysMs[Math.min(attempt, retryDelaysMs.length - 1)]
			);
			retryWakers.add(wake);
		});

	const sendUntilStored = async (
		queue: BlockSaveQueue,
		block: Block
	): Promise<BlockResponse> => {
		let consecutiveAuthFailures = 0;
		for (let attempt = 0; ; attempt += 1) {
			try {
				return await put(block);
			} catch (error) {
				consecutiveAuthFailures = isAuthError(error)
					? consecutiveAuthFailures + 1
					: 0;
				if (
					!isRetryableError(error) ||
					consecutiveAuthFailures >= maxConsecutiveAuthFailures
				) {
					throw error;
				}
				queue.failure = "retrying";
				queue.locked = true;
				notify();
				await waitBeforeRetry(attempt);
			}
		}
	};

	const drain = async (blockId: string, queue: BlockSaveQueue) => {
		queue.running = true;
		while (queue.saves.length > 0) {
			const head = queue.saves[0];
			queue.inFlight = true;
			if (queue.failure === "permanent") {
				queue.failure = null;
			}
			notify();
			const lockTimer = setTimeout(() => {
				if (!queue.locked) {
					queue.locked = true;
					notify();
				}
			}, lockAfterMs);
			try {
				const response = await sendUntilStored(queue, head.block);
				queue.failure = null;
				queue.failedBlock = null;
				head.resolve(response);
			} catch (error) {
				queue.failure = "permanent";
				queue.failedBlock = head.block;
				head.reject(error);
			} finally {
				clearTimeout(lockTimer);
				queue.saves.shift();
				queue.inFlight = false;
				if (queue.saves.length === 0) {
					queue.locked = false;
				}
				notify();
			}
		}
		queue.running = false;
		if (queue.failure === null) {
			queues.delete(blockId);
		}
		notify();
	};

	const getSnapshot = (): QuoteBlockSaveSnapshot => {
		let pendingCount = 0;
		let inFlightCount = 0;
		let failure: QuoteBlockSaveFailure | null = null;
		for (const queue of queues.values()) {
			pendingCount += queue.saves.length;
			inFlightCount += queue.inFlight ? 1 : 0;
			failure = worseBlockSaveFailure(failure, queue.failure);
		}
		return { pendingCount, inFlightCount, failure };
	};

	const subscribe = (listener: () => void) => {
		listeners.add(listener);
		return () => {
			listeners.delete(listener);
		};
	};

	const isSettledForRevision = () =>
		Array.from(queues.values()).every(
			queue => queue.saves.length === 0 || queue.failure === "retrying"
		);

	return {
		save: block => {
			const existing = queues.get(block.id);
			if (!enabled && !existing?.running) {
				return passthrough(block);
			}
			const queue: BlockSaveQueue = existing ?? {
				saves: [],
				running: false,
				inFlight: false,
				locked: false,
				failure: null,
				failedBlock: null
			};
			queues.set(block.id, queue);
			return new Promise<BlockResponse>((resolve, reject) => {
				queue.saves.push({ block, resolve, reject });
				if (queue.running) {
					notify();
				} else {
					void drain(block.id, queue);
				}
			});
		},
		setEnabled: value => {
			enabled = value;
			if (!value) {
				return;
			}
			queues.forEach((queue, blockId) => {
				if (!queue.running) {
					queues.delete(blockId);
				}
			});
			notify();
		},
		getSnapshot,
		getBlockState: blockId => {
			const queue = queues.get(blockId);
			if (queue?.failedBlock) {
				return {
					locked: true,
					failed: true,
					pendingBlock: queue.failedBlock
				};
			}
			if (queue?.locked) {
				return {
					locked: true,
					failed: false,
					pendingBlock:
						queue.saves[queue.saves.length - 1]?.block ?? null
				};
			}
			return UNLOCKED_BLOCK_STATE;
		},
		subscribe,
		waitForInFlight: () =>
			isSettledForRevision()
				? Promise.resolve()
				: new Promise(resolve => {
						const unsubscribe = subscribe(() => {
							if (isSettledForRevision()) {
								unsubscribe();
								resolve();
							}
						});
				  })
	};
};
